<template>
  <v-card
    elevation="3"
    class="my-5 card-fixed-height"
    :style="{ borderColor: theme.border }"
  >
    <v-card-title
      class="card-header py-3 px-4"
      :style="{ background: theme.headerBg, borderBottom: `1px solid ${theme.border}` }"
    >
      <v-icon :color="theme.main" class="mr-2 flex-shrink-0">
        {{ icon || theme.icon }}
      </v-icon>
      <span class="card-title-text" :style="{ color: theme.main }">{{ infoTitle }}</span>
      <v-chip
        v-if="hasData"
        x-small
        :color="theme.main"
        outlined
        class="ml-2 flex-shrink-0"
      >
        {{ conditions.length }}
      </v-chip>
      <v-tooltip v-if="showQuickAdd" bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            v-bind="attrs"
            v-on="on"
            class="flex-shrink-0 ml-1 card-quick-add"
            :aria-label="`Adicionar em ${infoTitle}`"
            @click="openQuickAdd"
          >
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </template>
        <span>Adicionar sem abrir o histórico</span>
      </v-tooltip>

      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            v-bind="attrs"
            v-on="on"
            class="flex-shrink-0 ml-1"
            @click="openDialog"
          >
            <v-icon data-tour="pd-card-menu">mdi-dots-vertical</v-icon>
          </v-btn>
        </template>
        <span>Ver e editar {{ infoTitle }}</span>
      </v-tooltip>
    </v-card-title>

    <v-divider></v-divider>

    <v-card-text class="pa-4 card-content">
      <!-- Estado vazio -->
      <div v-if="!hasData" class="text-center py-6">
        <v-icon size="48" color="grey lighten-1">{{ theme.icon }}</v-icon>
        <p class="text-caption grey--text mt-2 mb-0">
          Nada registrado ainda.
        </p>
        <v-btn
          small
          :color="theme.main"
          outlined
          class="mt-3"
          @click="openQuickAdd"
        >
          <v-icon small left>mdi-plus</v-icon>
          Adicionar
        </v-btn>
      </div>

      <!-- Lista de condições -->
      <div v-else class="conditions-list">
        <v-chip
          v-for="cond in conditions"
          :key="cond.id"
          class="condition-chip"
          :color="theme.chipBg"
          :text-color="theme.main"
          label
        >
          <strong v-if="cond.code" class="mr-1">{{ cond.code }}</strong>
          <span class="chip-text">{{ cond.description }}</span>
        </v-chip>
      </div>
    </v-card-text>

    <EditConditionsDialog
      v-model="dialogVisible"
      :open-direct-add="quickAddMode"
      :dialog-title="infoTitle"
      :conditions="conditions"
      :patient-id="patientId"
      :category="category"
      :mode="mode"
      :theme="theme"
      :readonly="readonly"
      @data-saved="handleDataSaved"
      @data-deleted="handleDataChanged"
    />
  </v-card>
</template>

<script>
import { getConditionsByPatient } from "@/services/conditionService";
import EditConditionsDialog from "@/features/patients/profile-dashboard/dialogs/EditConditionsDialog.vue";

const THEMES = {
  chronic: {
    main: "#E65100",
    headerBg: "linear-gradient(135deg, #FFF3E0 0%, #FFE0B2 100%)",
    border: "#FFCC80",
    chipBg: "#FFF3E0",
    icon: "mdi-medical-bag",
  },
  psychological: {
    main: "#6D28D9",
    headerBg: "linear-gradient(135deg, #F5F3FF 0%, #EDE9FE 100%)",
    border: "#C4B5FD",
    chipBg: "#F5F3FF",
    icon: "mdi-brain",
  },
  other: {
    main: "#546E7A",
    headerBg: "linear-gradient(135deg, #ECEFF1 0%, #CFD8DC 100%)",
    border: "#CFD8DC",
    chipBg: "#ECEFF1",
    icon: "mdi-clipboard-text-outline",
  },
};

import cardQuickAdd from "@/features/patients/profile-dashboard/mixins/cardQuickAdd.js";
import patientDataRefresh from "@/features/patients/profile-dashboard/mixins/patientDataRefresh";
import cardItemCount from "@/features/patients/profile-dashboard/mixins/cardItemCount.js";

export default {
  mixins: [patientDataRefresh, cardQuickAdd, cardItemCount],
  name: "ConditionsCard",

  components: {
    EditConditionsDialog,
  },

  props: {
    infoTitle: { type: String, default: "Condições" },
    patientId: { type: [String, Number], required: true },
    icon: { type: String, default: null },
    // Diferenciador: cada card mostra só a sua categoria.
    category: {
      type: String,
      default: "chronic",
      validator: (v) => ["chronic", "psychological", "other"].includes(v),
    },
    // Cards de crônicas/transtornos são só leitura (filtro). Adição é no card de CIDs.
    readonly: { type: Boolean, default: false },
  },

  data() {
    return {
      dialogVisible: false,
      conditions: [],
    };
  },

  computed: {
    /** SALVIA-938: itens do card, para o contador da caixa lateral. */
    itemCount() {
      return (this.conditions || []).length;
    },
    hasData() {
      return this.conditions.length > 0;
    },
    theme() {
      return THEMES[this.category] || THEMES.other;
    },
    // Doenças crônicas escolhem de um catálogo; o resto é texto livre.
    mode() {
      return this.category === "chronic" ? "catalog" : "text";
    },
  },

  watch: {
    patientId: "fetchData",
  },

  created() {
    this.fetchData().finally(() => this.$emit("card-loaded"));
  },

  methods: {
    async fetchData() {
      if (!this.patientId) return;
      try {
        this.conditions = await getConditionsByPatient(
          this.patientId,
          this.category,
        );
      } catch (error) {
        console.error("Erro ao buscar condições:", error);
        this.conditions = [];
      }
    },

    openDialog() {
      this.dialogVisible = true;
    },

    async handleDataChanged() {
      await this.fetchData();
      this.$emit("data-updated");
    },
  },
};
</script>

<style scoped>
.card-fixed-height {
  display: flex;
  flex-direction: column;
  height: 320px;
  border-radius: 14px !important;
  border: 1px solid;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}

.card-fixed-height:hover {
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12) !important;
}

.card-header {
  display: flex !important;
  flex-wrap: nowrap !important;
  align-items: center !important;
  min-width: 0;
}

.card-title-text {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.4;
  letter-spacing: 0.3px;
}

.card-content {
  flex: 1;
  overflow-y: auto;
}

.card-content::-webkit-scrollbar {
  width: 6px;
}
.card-content::-webkit-scrollbar-thumb {
  background: #cfd8dc;
  border-radius: 10px;
}

.conditions-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.condition-chip {
  font-size: 12px !important;
  font-weight: 600 !important;
  height: auto !important;
  min-height: 38px !important;
  padding: 8px 12px !important;
  white-space: normal !important;
  overflow-wrap: break-word;
  word-break: normal;
  width: 100%;
  display: flex !important;
  align-items: center;
  justify-content: flex-start;
  border-radius: 10px !important;
}

.chip-text {
  flex: 1;
  text-align: left;
  line-height: 1.4;
}
</style>
